import { useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "@/providers/auth";
import { useDomainServices } from "@/providers/DomainServices";
import type {
  DryRunReport,
  DryRunServices,
} from "@/shared/domain/boards/boardAuthoringService";
import type { ViewerControlChoices } from "@/shared/domain/boards/viewerControlChoices";

import { boardDryRunQueryOptions } from "./boardDryRunQuery";

export type BoardDryRunState =
  /** Nothing to dry-run: no definition yet, or no organization to run it in. */
  | { status: "not-run" }
  | { status: "loading" }
  | { status: "ready"; report: DryRunReport }
  | { status: "failed"; error: string };

/**
 * The authoring service's dry run of `definition`, for the confirmation
 * cards.
 *
 * The card must never wait on this: it renders the proposal from the tool
 * input on first paint and fills the per-panel status in as the report
 * lands, so Approve is live from the start. The report is cached under the
 * key the tool's `execute` reads after approval, so the card's run is the
 * approval's run; and the dry run fetches under the board page's own
 * cache keys, so the page's first render shares one fetch per query with it.
 * The query itself, including why a card's unmount must not cancel its run,
 * is `boardDryRunQueryOptions`. `storedDefinition` is the stored definition
 * an update was applied to; every stage still runs, and a panel it holds
 * with the same content, under the same time range and over the same
 * declarations of the stages and controls it depends on, skips its rendering
 * checks once its stages pass. `viewerChoices` are the values the viewer
 * chose in the controls of the board on screen, which the stages run with.
 */
export function useBoardDryRun(
  definition: unknown,
  storedDefinition?: unknown,
  viewerChoices?: ViewerControlChoices,
): BoardDryRunState {
  const { currentOrganization } = useAuth();
  const services = useDryRunServices();
  const queryClient = useQueryClient();
  const orgId = currentOrganization?.org_id;
  const canRun = definition !== undefined && orgId !== undefined;

  const query = useQuery({
    ...boardDryRunQueryOptions({
      definition,
      // The query is disabled without an org, so the placeholder never runs.
      orgId: orgId ?? "",
      services,
      queryClient,
      storedDefinition,
      viewerChoices,
    }),
    enabled: canRun,
  });

  if (!canRun) {
    return { status: "not-run" };
  }
  if (query.data !== undefined) {
    return { status: "ready", report: query.data };
  }
  if (query.isError) {
    return {
      status: "failed",
      error:
        query.error instanceof Error
          ? query.error.message
          : String(query.error),
    };
  }
  return { status: "loading" };
}

/**
 * The services a confirmation card hands the authoring service for a dry run
 * or a patch, from the app's domain services.
 */
export function useDryRunServices(): DryRunServices {
  const {
    customFieldService,
    metricsService,
    queryService,
    sessionService,
    topics,
  } = useDomainServices();
  return {
    customFieldService,
    metricsService,
    queryService,
    sessionService,
    topicService: topics,
  };
}
